import api from './api';
import { Gauntlet, GauntletRun, Match, EloRating, RadarData } from '../types/gymnasium';

export const gymnasiumService = {
  // ── Gauntlets ──────────────────────────────────────────────────────
  listGauntlets: async (tags?: string[], search?: string): Promise<Gauntlet[]> => {
    const params: any = {};
    if (tags?.length) params.tags = tags.join(',');
    if (search) params.search = search;
    const resp = await api.get('/gymnasium/gauntlets', { params });
    return resp.data;
  },

  getGauntlet: async (id: string): Promise<Gauntlet> => {
    const resp = await api.get(`/gymnasium/gauntlets/${id}`);
    return resp.data;
  },

  generateGauntlet: async (intent: string, count = 6): Promise<any> => {
    const resp = await api.post('/gymnasium/gauntlets/generate', { intent, count });
    return resp.data;
  },

  createGauntlet: async (data: any): Promise<Gauntlet> => {
    const resp = await api.post('/gymnasium/gauntlets', data);
    return resp.data;
  },

  updateGauntlet: async (id: string, data: any): Promise<Gauntlet> => {
    const resp = await api.put(`/gymnasium/gauntlets/${id}`, data);
    return resp.data;
  },

  deleteGauntlet: async (id: string): Promise<void> => {
    await api.delete(`/gymnasium/gauntlets/${id}`);
  },

  // ── Runs ───────────────────────────────────────────────────────────
  startRun: async (gauntletId: string, personaId: string, judgeMode = 'auto'): Promise<{ run_id: string }> => {
    const resp = await api.post(`/gymnasium/gauntlets/${gauntletId}/run`, {
      persona_id: personaId,
      judge_mode: judgeMode,
    });
    return resp.data;
  },

  getRunStatus: async (runId: string): Promise<GauntletRun> => {
    const resp = await api.get(`/gymnasium/runs/${runId}`);
    return resp.data;
  },

  listRuns: async (personaId?: string, gauntletId?: string): Promise<any[]> => {
    const params: any = {};
    if (personaId) params.persona_id = personaId;
    if (gauntletId) params.gauntlet_id = gauntletId;
    const resp = await api.get('/gymnasium/runs', { params });
    return resp.data;
  },

  submitJudgment: async (runId: string, data: any): Promise<void> => {
    await api.post(`/gymnasium/runs/${runId}/judge`, data);
  },

  compareRuns: async (runIds: string[]): Promise<any> => {
    const resp = await api.post('/gymnasium/compare', { run_ids: runIds });
    return resp.data;
  },

  // ── Matches ────────────────────────────────────────────────────────
  createMatch: async (personaIds: string[], prompt: string, category?: string, probeSource?: any): Promise<Match> => {
    const resp = await api.post('/gymnasium/matches', {
      persona_ids: personaIds,
      prompt,
      category,
      probe_source: probeSource || undefined,
    });
    return resp.data;
  },

  getMatch: async (id: string): Promise<Match> => {
    const resp = await api.get(`/gymnasium/matches/${id}`);
    return resp.data;
  },

  recordVote: async (matchId: string, winner: string, notes?: string): Promise<Match> => {
    const resp = await api.post(`/gymnasium/matches/${matchId}/vote`, { winner, notes });
    return resp.data;
  },

  autoJudgeMatch: async (matchId: string): Promise<any> => {
    const resp = await api.post(`/gymnasium/matches/${matchId}/auto-judge`);
    return resp.data;
  },

  runMatchSeries: async (personaIds: string[], prompts: { prompt: string; category?: string; probe_source?: any }[]): Promise<any> => {
    const resp = await api.post('/gymnasium/matches/series', { persona_ids: personaIds, prompts });
    return resp.data;
  },

  getArenaProbes: async (personaIds: string[]): Promise<any> => {
    const resp = await api.post('/gymnasium/arena/probes', { persona_ids: personaIds });
    return resp.data;
  },

  listMatches: async (personaId?: string): Promise<any[]> => {
    const params: any = {};
    if (personaId) params.persona_id = personaId;
    const resp = await api.get('/gymnasium/matches', { params });
    return resp.data;
  },

  // ── Rankings & Radar ───────────────────────────────────────────────
  getRankings: async (): Promise<{ ratings: EloRating[]; match_count: number; last_computed?: string }> => {
    const resp = await api.get('/gymnasium/rankings');
    return resp.data;
  },

  getPersonaRanking: async (personaId: string): Promise<EloRating | null> => {
    const resp = await api.get(`/gymnasium/rankings/${personaId}`);
    return resp.data;
  },

  getRadarData: async (personaIds: string[], source = 'gymnasium'): Promise<RadarData> => {
    const resp = await api.post('/gymnasium/radar', { persona_ids: personaIds, source });
    return resp.data;
  },
};
